import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, PackageCheck, Trash2, Wrench } from 'lucide-react';
import { PERMISSIONS, ReturnDisposition } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useCancelReturnMutation,
  useInspectReturnLineMutation,
  useReturnQuery,
} from '../api/returns.api';

const DISPOSITION_TONES: Record<ReturnDisposition, 'warning' | 'success' | 'danger' | 'neutral'> = {
  QUARANTINE: 'warning',
  RESTOCK: 'success',
  DAMAGED: 'danger',
  DISPOSED: 'neutral',
};

/**
 * One return, line by line.
 *
 * Inspection is the act that decides where the goods go, so it lives here rather than on the list:
 * someone has to look at the frame before it can be called sellable again, and only a restock puts
 * anything back on the shelf. Taking the return needs `sales.refund`; deciding its fate needs
 * `inventory.adjust`, which is why the two sets of buttons are gated separately.
 */
export function ReturnDetailPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { returnId = '' } = useParams<{ returnId: string }>();

  const salesReturn = useReturnQuery(returnId);
  const inspect = useInspectReturnLineMutation(returnId);
  const cancel = useCancelReturnMutation(returnId);

  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const canInspect = can(PERMISSIONS.INVENTORY_ADJUST);
  const canCancel = can(PERMISSIONS.SALES_REFUND);

  if (salesReturn.isPending) return <LoadingState />;
  if (salesReturn.isError || !salesReturn.data) {
    return (
      <Alert tone="error">
        {apiErrorMessage(salesReturn.error) ?? t('sales.returns.errors.load')}
      </Alert>
    );
  }

  const data = salesReturn.data;
  const isOpen = data.status === 'OPEN';
  const quarantined = data.lines.filter((line) => line.disposition === 'QUARANTINE');

  const decide = (lineId: string, disposition: ReturnDisposition) => {
    setError(null);
    inspect.mutate(
      { lineId, disposition },
      {
        onSuccess: () => {
          setFeedback(t(`sales.returns.inspected.${disposition}`));
        },
        onError: (caught) => {
          setFeedback(null);
          setError(apiErrorMessage(caught) ?? t('sales.returns.errors.inspect'));
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={data.reference}
        subtitle={[data.customerName, data.branchName].filter(Boolean).join(' · ')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => navigate('/sales/returns')}>
              <ArrowLeft className="size-4 rtl:rotate-180" aria-hidden />
              {t('sales.returns.backToList')}
            </Button>
            {canCancel && isOpen && quarantined.length === data.lines.length ? (
              <Button variant="danger" onClick={() => setCancelOpen(true)}>
                {t('sales.returns.cancelReturn')}
              </Button>
            ) : null}
          </div>
        }
      />

      {error ? <Alert tone="error">{error}</Alert> : null}
      {feedback ? <Alert tone="success">{feedback}</Alert> : null}

      <div className="flex flex-wrap items-center gap-3">
        <Badge tone={isOpen ? 'brand' : 'neutral'}>
          {t(`sales.returns.statuses.${data.status}`)}
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/sales/optical/${data.orderId}`)}
        >
          <span className="font-mono" dir="ltr">
            {data.orderReference}
          </span>
        </Button>
        {data.quarantinedQuantity > 0 ? (
          <Badge tone="warning">
            {t('sales.returns.awaitingInspection', { count: data.quarantinedQuantity })}
          </Badge>
        ) : (
          <span className="text-muted text-xs">{t('sales.returns.allInspected')}</span>
        )}
        <span className="text-muted ms-auto text-xs">{data.createdByName}</span>
      </div>

      {data.reason ? <Alert tone="info">{data.reason}</Alert> : null}
      {data.status === 'CANCELLED' && data.cancelledReason ? (
        <Alert tone="warning">
          {t('sales.returns.cancelledBecause', { reason: data.cancelledReason })}
        </Alert>
      ) : null}

      <Card>
        <CardHeader title={t('sales.returns.lines')} subtitle={t('sales.returns.linesHint')} />

        <ul className="divide-line divide-y">
          {data.lines.map((line) => (
            <li key={line.id} className="flex flex-wrap items-center gap-3 p-4">
              <span className="min-w-0 flex-1">
                <span className="text-ink block truncate font-medium">{line.productName}</span>
                <span className="text-muted font-mono text-xs" dir="ltr">
                  {line.sku}
                  {line.batchNumber ? ` · ${line.batchNumber}` : ''}
                </span>
                {line.inspectedByName ? (
                  <span className="text-muted block text-xs">
                    {t('sales.returns.inspectedBy', { name: line.inspectedByName })}
                  </span>
                ) : null}
              </span>

              <span className="text-ink font-medium" dir="ltr">
                {line.quantity}
              </span>

              <Badge tone={DISPOSITION_TONES[line.disposition] ?? 'neutral'}>
                {t(`sales.returns.dispositions.${line.disposition}`)}
              </Badge>

              {canInspect && isOpen && line.disposition === 'QUARANTINE' ? (
                <span className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    isLoading={inspect.isPending}
                    onClick={() => decide(line.id, ReturnDisposition.RESTOCK)}
                  >
                    <PackageCheck className="size-4" aria-hidden />
                    {t('sales.returns.actions.restock')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    isLoading={inspect.isPending}
                    onClick={() => decide(line.id, ReturnDisposition.DAMAGED)}
                  >
                    <Wrench className="size-4" aria-hidden />
                    {t('sales.returns.actions.damaged')}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    isLoading={inspect.isPending}
                    onClick={() => decide(line.id, ReturnDisposition.DISPOSED)}
                  >
                    <Trash2 className="size-4" aria-hidden />
                    {t('sales.returns.actions.disposed')}
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>

        {isOpen && quarantined.length > 0 ? (
          <div className="border-line border-t p-4">
            <Alert tone="warning">{t('sales.returns.quarantineNotice')}</Alert>
          </div>
        ) : null}
      </Card>

      <Modal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={t('sales.returns.cancelTitle')}
        description={t('sales.returns.cancelHint')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              isLoading={cancel.isPending}
              disabled={cancelReason.trim().length < 3}
              onClick={() =>
                cancel.mutate(cancelReason.trim(), {
                  onSuccess: () => {
                    setCancelOpen(false);
                    setCancelReason('');
                    setError(null);
                    setFeedback(t('sales.returns.cancelledOk'));
                  },
                  onError: (caught) => {
                    setCancelOpen(false);
                    setFeedback(null);
                    setError(apiErrorMessage(caught) ?? t('sales.returns.errors.cancel'));
                  },
                })
              }
            >
              {t('sales.returns.cancelReturn')}
            </Button>
          </>
        }
      >
        <FormField label={t('sales.returns.cancelReason')} required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={cancelReason}
              maxLength={500}
              onChange={(event) => setCancelReason(event.target.value)}
              placeholder={t('sales.returns.cancelReasonPlaceholder')}
            />
          )}
        </FormField>
      </Modal>
    </div>
  );
}
